/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { Injectable, Type } from "@angular/core";
import {
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
  PRIMITIVE,
} from "shared/provider-webworker";
import { ILookupService } from "../ILookupService";

import { BaseLookupService } from "./BaseLookupService";
import { PromiseLookupService } from "./PromiseLookupService";
import { PromiseSet } from "../../promise/PromiseSet";
import { PrimaryPromiseSet } from "../../promise/PrimaryPromiseSet";
import { Subject } from "rxjs";

import { CubiqWorker } from "../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { Serializer } from "shared/provider-webworker";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export abstract class PrimaryLookupService<
  K,
  V extends CubiqTS.ConcreteEncodable,
> extends PromiseLookupService<K, V> {
  protected broker: ServiceMessageBroker;
  protected _primed: PromiseSet<boolean> = new PrimaryPromiseSet<boolean>();
  protected readonly VALUE: CubiqTS.ConcreteEncodable | CubiqTS.ConcreteEncodable[];
  protected readonly EVENT_VALUE: CubiqTS.ConcreteEncodable;
  protected _serializer: Serializer = new CubiqTS.CubiqSerializer();

  protected modelUpdateEventSubject: Subject<V> = new Subject<V>();
  protected modelUpdateEventPorts: Array<MessagePort> = new Array<MessagePort>();
  protected modelUpdateEventListeners: any;

  constructor(
    protected base: BaseLookupService<K, V>,
    protected factory: ServiceMessageBrokerFactory,
    protected lookupType: Type<CubiqTS.ConcreteEncodable> | Array<Type<CubiqTS.ConcreteEncodable>>,
    protected cubiqWorker: CubiqWorker,
    protected serviceChannel: string,
    protected modelUpdateEventType: Type<CubiqTS.ConcreteEncodable> = CubiqTS.ModelUpdateImpl,
  ) {
    super();

    this.VALUE = Array.isArray(lookupType)
      ? CubiqTS.MIXED_ENCODABLE(lookupType)
      : CubiqTS.ENCODABLE(lookupType);
    this.EVENT_VALUE = CubiqTS.ENCODABLE(modelUpdateEventType);

    this.broker = this.factory.createMessageBroker(this.serviceChannel, true, true);

    this.broker.registerMethod("isPrimed", [], this.isPrimed.bind(this), PRIMITIVE);
    this.broker.registerMethod("prime", [this.VALUE], this.prime.bind(this), PRIMITIVE);
    this.broker.registerMethod("getItemKey", [this.VALUE], this.getItemKey.bind(this), PRIMITIVE);
    this.broker.registerMethod("getItem", [PRIMITIVE], this.getItem.bind(this), this.VALUE);
    this.broker.registerMethod("createItem", [this.VALUE], this.createItem.bind(this), this.VALUE);
    this.broker.registerMethod("updateItem", [this.VALUE], this.updateItem.bind(this), this.VALUE);
    this.broker.registerMethod("deleteItem", [this.VALUE], this.deleteItem.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "deleteItemById",
      [PRIMITIVE],
      this.deleteItemById.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "lookupItem",
      [PRIMITIVE, PRIMITIVE],
      this.lookupItem.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod("getAll", [], this.getAll.bind(this), this.VALUE);
    this.broker.registerMethod(
      "initModelUpdateService",
      [PRIMITIVE],
      this.initModelUpdateService.bind(this),
      null,
      false,
    );

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ModelUpdateImpl.name)
      .subscribe(this.processModelUpdateEvent.bind(this));
  }

  public registerModelUpdateEventListener(modelType: Type<any>, method): void {
    if (!this.modelUpdateEventListeners) {
      this.modelUpdateEventListeners = {};
    }

    this.modelUpdateEventListeners[modelType.name] = method;
  }

  protected processModelUpdateEvent(event: CubiqTS.ModelUpdateImpl): void {
    if (
      event == Object(event) &&
      event.payLoad == Object(event.payLoad) &&
      ((Array.isArray(this.lookupType) &&
        this.lookupType.some((type) => {
          event.payLoad instanceof type;
        })) ||
        (Array.isArray(this.lookupType) === false &&
          event.payLoad instanceof (this.lookupType as Function)))
    ) {
      let payload: V = event.payLoad as V;

      if (event.eventType == CubiqTS.ModelUpdateType.DELETE) {
        this.base.deleteItem(payload);
      } else {
        this.base.mapItem(payload);
      }

      this.fireModelUpdateEvent(event);
    }
    // disabled for now, reprime of blotter etc when they get cahce updates
    // if (event == Object(event) && event.payLoad == Object(event.payLoad) &&
    //     this.modelUpdateEventListeners && this.modelUpdateEventListeners[event.payLoad.constructor.name]) {
    //   let allItems: V[] = this.base.getAll();
    //   for (let i: number = 0; i < allItems.length; i++) {
    //     let update: boolean = this.modelUpdateEventListeners[event.payLoad.constructor.name].call(this, event.payLoad, allItems[i]);
    //     let updateEvent: any = update ? this.createModelUpdateEvent(allItems[i]) : null;

    //     if (updateEvent) {
    //       setTimeout(this.fireModelUpdateEvent.bind(this, updateEvent), 100);
    //     }
    //   }
    // }
  }

  protected fireModelUpdateEvent(event: any): void {
    if (event) {
      // let serializedEvent: any = this._serializer.serialize(event, this.EVENT_VALUE);
      // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
      //   try {
      //     port.postMessage(serializedEvent);
      //   } catch (ex) {
      //     logger.out.error(ex);
      //   }
      // });

      this.modelUpdateEventSubject.next(event);
    }
  }

  protected createModelUpdateEvent(updatedItem: any): any {
    return null;
  }

  protected initModelUpdateService(port: MessagePort): void {
    this.modelUpdateEventPorts.push(port);
  }

  isPrimed(): Promise<boolean> {
    logger.out.info(
      `${this.constructor.name} isPrimed() ` +
        `request for channel ${this.serviceChannel} PromiseSet ` +
        `isPending:${this._primed.isPending()} ` +
        `isResolved:${this._primed.isResolved()} ` +
        `isRejected:${this._primed.isRejected()}`,
    );

    return this._primed.get();
  }

  prime(values: Array<V>): Promise<void> {
    return Promise.resolve(this.base.prime(values));
  }

  getItemKey(item: V): Promise<K> {
    return new Promise<K>((res, rej) => {
      res(this.base.getItemKey(item));
    });
  }

  getItem(id: K): Promise<V> {
    return new Promise<V>((res, rej) => {
      res(this.base.getItem(id));
    });
  }

  createItem(item: V): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      res(this.base.createItem(item));
    });
  }

  updateItem(item: V): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      res(this.base.updateItem(item));
    });
  }

  deleteItem(item: V): Promise<void> {
    return new Promise<void>((res, rej) => {
      res(this.base.deleteItem(item));
    });
  }

  deleteItemById(id: K): Promise<void> {
    return new Promise<void>((res, rej) => {
      res(this.base.deleteItemById(id));
    });
  }

  getAll(): Promise<Array<V>> {
    if (!this.base.isPrimed) {
      return new Promise<Array<V>>((res, rej) => {
        logger.out.warn(
          `${this.constructor.name} PrimaryLookupService getAll() called without isPrimed() guard`,
        );

        this.isPrimed().then(
          (isPrimed) => {
            if (isPrimed) {
              res(this.base.getAll());
            } else {
              rej(`${this.constructor.name} isPrimed false`);
            }
          },
          (error) => {
            rej(error);
          },
        );
      });
    }

    return new Promise<Array<V>>((res, rej) => {
      res(this.base.getAll());
    });
  }

  getBase(): any {
    return this.base;
  }

  getModelUpdateSubject(): Subject<V> {
    return this.modelUpdateEventSubject;
  }

  lookupItem(propertyName: string, substring: string): Promise<Array<V>> {
    return new Promise<Array<V>>((res, rej) => {
      res(
        this.base
          .getAll()
          .filter(
            (a) =>
              a &&
              a[propertyName] !== undefined &&
              a[propertyName] != null &&
              typeof a[propertyName] == "string" &&
              (a[propertyName] as string).toLowerCase().indexOf(substring.toLowerCase()) >= 0,
          ),
      );
    });
  }

  protected compareIds(id1: CubiqTS.Long, id2: CubiqTS.Long): boolean {
    return id1 && id2 && id1.toNumber() == id2.toNumber();
  }
}
